import { computed, getCurrentScope, onScopeDispose, ref, shallowRef } from 'vue'
import {
  ApiError,
  fetchTrafficCustomEvents,
  fetchTrafficPages,
  fetchTrafficSources,
  fetchTrafficVisitors,
  type TrafficCustomEventAnalytics,
  type TrafficPageAnalytics,
  type TrafficPeriodSelection,
  type TrafficSourceAnalytics,
  type TrafficVisitorAnalytics
} from '@xjlv2/api-client'
import type { TrafficDimensionApi } from '@xjlv2/api-client'
import type { TrafficWorkbenchApi, TrafficWorkbenchOptions } from '../types'

export type { TrafficWorkbenchApi, TrafficWorkbenchOptions } from '../types'

/** 行为分析四子视图：来源 / 访客 / 受访页 / 自定义事件。 */
export type BehaviorAnalyticsSubview = 'sources' | 'visitors' | 'pages' | 'events'

export type BehaviorAnalyticsData =
  | TrafficSourceAnalytics
  | TrafficVisitorAnalytics
  | TrafficPageAnalytics
  | TrafficCustomEventAnalytics

export interface BehaviorAnalyticsRequest {
  subview?: BehaviorAnalyticsSubview
  period?: TrafficPeriodSelection
  /** null = 全部模块（请求省略 moduleCode）。 */
  moduleCode?: string | null
}

const DEFAULT_PERIOD: TrafficPeriodSelection = { preset: 'THIS_MONTH' }
/** 报表榜单固定 TopN 10（后端合法域 1..50）。 */
const BEHAVIOR_ANALYTICS_LIMIT = 10

/**
 * 行为分析四视图取数：按 subview 惰性请求维度端点，已加载视图按当前
 * period+module 缓存（筛选变更即失效）；requestSequence+AbortController
 * 竞态防护照 useTrafficWorkbench 模式——旧请求迟到响应一律丢弃。
 */
export function useBehaviorAnalytics(options: TrafficWorkbenchOptions) {
  const subview = ref<BehaviorAnalyticsSubview>('sources')
  const period = ref<TrafficPeriodSelection>({ ...DEFAULT_PERIOD })
  const moduleCode = ref<string | null>(null)
  // shallowRef：整包载荷按引用替换，避免深代理开销并保持载荷引用稳定。
  const data = shallowRef<BehaviorAnalyticsData | null>(null)
  const loading = ref(false)
  const error = ref('')
  const featureEnabled = ref(true)
  const canView = computed(() => options.canView())

  const cacheBySubview = new Map<BehaviorAnalyticsSubview, BehaviorAnalyticsData>()
  let requestSequence = 0
  let activeController: AbortController | null = null

  async function load(request: BehaviorAnalyticsRequest = {}): Promise<void> {
    const nextSubview = request.subview ?? subview.value
    const nextPeriod = request.period ? { ...request.period } : { ...period.value }
    const nextModule = request.moduleCode !== undefined ? request.moduleCode : moduleCode.value

    if (!isSamePeriod(period.value, nextPeriod) || moduleCode.value !== nextModule) {
      cacheBySubview.clear()
    }
    subview.value = nextSubview
    period.value = nextPeriod
    moduleCode.value = nextModule

    if (!canView.value) {
      reset()
      return
    }

    const cached = cacheBySubview.get(nextSubview)
    if (cached) {
      // 命中缓存即同步呈现：作废在途请求并丢弃其迟到响应。
      requestSequence += 1
      activeController?.abort()
      activeController = null
      data.value = cached
      loading.value = false
      error.value = ''
      return
    }

    const sequence = ++requestSequence
    activeController?.abort()
    const controller = new AbortController()
    activeController = controller
    loading.value = true
    error.value = ''
    data.value = null

    try {
      const client = options.createApiClient()
      // 维度 fetch 契约只收 path；经此包装把 abort 信号透传到底层 ApiClient。
      const api: TrafficDimensionApi = {
        get: <T>(path: string) => client.get<T>(path, { signal: controller.signal })
      }
      const response = await fetchSubview(api, nextSubview, nextModule, nextPeriod)
      if (sequence !== requestSequence) return

      featureEnabled.value = true
      cacheBySubview.set(nextSubview, response)
      data.value = response
    } catch (cause) {
      if (sequence !== requestSequence || isAbortError(cause)) return

      if (isAnalyticsDisabled(cause)) {
        featureEnabled.value = false
        cacheBySubview.clear()
        data.value = null
        error.value = ''
        return
      }

      error.value = describeBehaviorAnalyticsError(cause)
      data.value = null
    } finally {
      if (sequence === requestSequence) {
        loading.value = false
        activeController = null
      }
    }
  }

  function reset(): void {
    requestSequence += 1
    activeController?.abort()
    activeController = null
    cacheBySubview.clear()
    data.value = null
    loading.value = false
    error.value = ''
    featureEnabled.value = true
  }

  if (getCurrentScope()) {
    onScopeDispose(reset)
  }

  return {
    data,
    loading,
    error,
    subview,
    period,
    moduleCode,
    canView,
    featureEnabled,
    load,
    reset
  }
}

async function fetchSubview(
  api: TrafficDimensionApi,
  subview: BehaviorAnalyticsSubview,
  moduleCode: string | null,
  period: TrafficPeriodSelection
): Promise<BehaviorAnalyticsData> {
  switch (subview) {
    case 'sources':
      return fetchTrafficSources(api, moduleCode, period, BEHAVIOR_ANALYTICS_LIMIT)
    case 'visitors':
      return fetchTrafficVisitors(api, moduleCode, period, BEHAVIOR_ANALYTICS_LIMIT)
    case 'pages':
      return fetchTrafficPages(api, moduleCode, period, BEHAVIOR_ANALYTICS_LIMIT)
    case 'events':
      return fetchTrafficCustomEvents(api, moduleCode, period, BEHAVIOR_ANALYTICS_LIMIT)
  }
}

function isSamePeriod(left: TrafficPeriodSelection, right: TrafficPeriodSelection): boolean {
  return (
    left.preset === right.preset &&
    (left.from ?? '') === (right.from ?? '') &&
    (left.until ?? '') === (right.until ?? '')
  )
}

/** 后端 TRAFFIC_* 错误码 → 用户可读文案；未知错误透传 message。 */
export function describeBehaviorAnalyticsError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.code === 'TRAFFIC_PERIOD_INVALID') return '查询区间不合法，请调整统计周期'
    if (cause.code === 'TRAFFIC_LIMIT_INVALID') return '榜单条数必须在 1 到 50 之间'
    return cause.message
  }
  if (cause instanceof Error && cause.message.startsWith('CUSTOM traffic period')) {
    // buildTrafficPeriodQuery 的本地校验（缺日期/区间倒置/超 366 天）。
    return '请选择有效的开始和结束日期'
  }
  return cause instanceof Error && cause.message ? cause.message : '行为分析数据加载失败'
}

function isAnalyticsDisabled(cause: unknown): boolean {
  return cause instanceof ApiError && cause.code === 'TRAFFIC_ANALYTICS_DISABLED'
}

function isAbortError(cause: unknown): boolean {
  return cause instanceof Error && cause.name === 'AbortError'
}
